<script lang="ts">
	import { t } from '../../i18n/i18n'
	import { analyticNames, findValue } from './stores'
	import type { Writable } from 'svelte/store'
	import { getContext, onDestroy, onMount } from 'svelte'
	import type { DangerAssessment, EvaluationResult } from './types/analyticalEvaluation'
	import DangerBadge from '../atoms/badges/DangerBadge.svelte'
	import AnalysisScaleBadge from './AnalysisScaleBadge.svelte'

	export let evaluationResult: Record<string, EvaluationResult>

	export let valueMetadata = {}

	export let danger: DangerAssessment | undefined = undefined

	export let combinedMaxOrdinals: Record<string, number> = {}

	let evaluationContainer: HTMLElement
	let resizeObserver: ResizeObserver
	let stickyHeightInformation: Writable<string> = getContext('stickyHeightInformation')

	function handleClick(value) {
		let input = document.getElementById(value)?.querySelector('input')
		let row = document.getElementById(value)

		if (input && row) {
			if (input.disabled) {
				row.scrollIntoView({ block: 'center', behavior: 'smooth' })
				document.getElementById(value).querySelector('td').style.animation = 'color-change 2s 1'
				setTimeout(() => {
					document.getElementById(value).querySelector('td').style.animation = ''
				}, 2000)
			} else {
				input.focus()
			}
		}
	}

	onMount(() => {
		resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				$stickyHeightInformation = (<HTMLElement>entry.target).offsetHeight + 'px'
			}
		})

		resizeObserver.observe(evaluationContainer)
	})

	onDestroy(() => {
		if (resizeObserver) {
			resizeObserver.disconnect()
		}
	})

	$: dangerRangeExceeded = !!danger?.dangerRangeValues?.length
	$: ecotoxic = !!danger?.ecotoxicValues?.length
</script>

<div bind:this={evaluationContainer} class="evaluationContainer">
	<div class="innerContainer scrollable">
		{#if evaluationResult}
			{#each Object.entries(evaluationResult) as [analyticTechnicalName, result], i}
				{@const maxOrdinal = combinedMaxOrdinals[analyticTechnicalName]}
				<div class="header" style="grid-row-start: {2 * i + 1}">
					{$t('Analytics.testMethod')}
				</div>
				<div class="header" style="grid-row-start: {2 * i + 1}">
					{$t('Analytics.evaluation')}
				</div>
				<div class="header" style="grid-row-start: {2 * i + 1}">
					{$t('Analytics.maxValue')}
				</div>
				<div class="header" style="grid-row-start: {2 * i + 1}">
					{$t('Analytics.missingValues')}
				</div>

				<div class="procedure subrow" style="grid-row-start: {2 * i + 2}">
					{$analyticNames[analyticTechnicalName]}
				</div>
				<div class="evaluation subrow" style="grid-row-start: {2 * i + 2}">
					{#if result.result.description === '/' || maxOrdinal === undefined}
						<!-- no scale to colour the badge with, so the plain description has to do -->
						{result.result.description}
					{:else}
						<AnalysisScaleBadge
							ordinal={result.result.priority}
							{maxOrdinal}
							description={result.result.description}
							uppercase
						/>
					{/if}
					{#if dangerRangeExceeded || ecotoxic}
						<DangerBadge/>
					{/if}
				</div>
				<div class="worst subrow scrollable" style="grid-row-start: {2 * i + 2}">
					{#each result.worstValues as worstValue}
						{@const worstVal = findValue(worstValue)}
						<span class="focus-value" on:click={handleClick(valueMetadata[worstValue]?.key)}>
							{valueMetadata[worstValue].name}: {worstVal?.value}
							{worstVal?.unit || ''}
						</span><br />
					{/each}
				</div>
				<div class="missing subrow scrollable" style="grid-row-start: {2 * i + 2}">
					{#each result.missingValues as missingValue}
						<span class="focus-value" on:click={handleClick(valueMetadata[missingValue].key)}>
							{valueMetadata[missingValue].name}
						</span><br />
					{/each}
				</div>
			{/each}
		{:else}
			<div class="header" style="grid-row-start: {1}">
				{$t('Analytics.testMethod')}
			</div>
			<div class="header" style="grid-row-start: {1}">
				{$t('Analytics.evaluation')}
			</div>
			<div class="header" style="grid-row-start: {1}">
				{$t('Analytics.maxValue')}
			</div>
			<div class="header" style="grid-row-start: {1}">
				{$t('Analytics.missingValues')}
			</div>

			<div class="procedure subrow" style="grid-row-start: {2}">/</div>
			<div class="evaluation subrow" style="grid-row-start: {2}">/</div>
			<div class="worst subrow scrollable" style="grid-row-start: {2}">/</div>
			<div class="missing subrow scrollable" style="grid-row-start: {2}">/</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@import '$styles/global';

	.evaluationContainer {
		width: 100%;
		border: 1px solid $grey-700;
		border-radius: 4px;
		box-shadow: 0 0 6px rgba(0, 0, 0, 0.25);
		grid-template-columns: auto 120px 120px 120px;
		grid-template-rows: auto auto;
		padding: 20px 24px 18px 24px;
	}

	.innerContainer {
		display: grid;
		width: 100%;
		max-height: 200px;
		overflow: hidden auto;
		padding-right: 20px;
		padding-bottom: 2px;
	}

	.header {
		@include roboto-font(0.875rem, 500, 0.75rem, #999999);

		text-transform: uppercase;
		margin-top: 6px;
	}

	.subrow {
		margin-top: 6px;
	}

	.scrollable::-webkit-scrollbar {
		width: 4px;
	}

	/* Track */
	.scrollable::-webkit-scrollbar-track {
		background: $bluish-grey-200;
		border-radius: 4px;
		margin-top: 16px;
		margin-bottom: 16px;
		margin-right: 12px;
	}

	/* Handle */
	.scrollable::-webkit-scrollbar-thumb {
		background: $primaryGreen;
		border-radius: 4px;
	}

	.procedure {
		grid-column: 1 / 1;

		@include roboto-font(1rem, 500, 0.875rem);
	}

	.evaluation {
		grid-column: 2 / 2;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;

		@include roboto-font(1rem, 500, 0.875rem);
	}

	.worst {
		grid-column: 3 / 3;
		max-height: 100px;
		overflow: hidden auto;

		@include roboto-font(0.75rem, 500, 0.625rem);
	}

	.missing {
		grid-column: 4 / 4;
		max-height: 100px;
		overflow: hidden auto;

		@include roboto-font(0.75rem, 500, 0.625rem, #f24822);
	}

	.focus-value {
		color: black;
	}

	.focus-value:hover {
		cursor: pointer;
		color: $primaryGreen;
	}

	@keyframes -global-color-change {
		0% {
			color: #212529;
		}

		25% {
			color: $primaryGreen;
		}

		75% {
			color: $primaryGreen;
		}

		100% {
			color: #212529;
		}
	}
</style>
